import { useTranslation } from 'react-i18next'
import { MapPin, Calendar, ArrowUpRight } from 'lucide-react'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

interface EventCardProps {
  image: string
  imageAlt?: string
  title: string
  location: string
  /** ISO date — e.g. "2023-03-29" or a date-range string passthrough */
  date: string
  slug: string
  className?: string
}

function formatDate(dateStr: string, lang: SupportedLanguage): string {
  const parsed = new Date(dateStr)
  if (isNaN(parsed.getTime())) return dateStr
  return parsed.toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * EventCard — industrial trade-show / fair card. Visually distinguished from
 * NewsCard by a bold brand-blue sidebar ribbon with the event date pulled
 * out in large type — like a coordinate label on a technical drawing.
 */
export function EventCard({
  image,
  imageAlt,
  title,
  location,
  date,
  slug,
  className,
}: EventCardProps) {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const eventsSegment = localizedRoutes.events[currentLang]
  const href = `/${currentLang}/${eventsSegment}/${slug}`

  const handleNavigate = () => {
    navigate(`${eventsSegment}/${slug}`)
  }

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col bg-white fx-card-border rounded-sm shadow-sm overflow-hidden',
        'transition-[transform,box-shadow,border-color] duration-300 ease-out',
        'hover:-translate-y-0.5 hover:shadow-md hover:border-brand-400',
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden bg-ink-100">
        <img
          src={image}
          alt={imageAlt ?? title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
        {/* Brand accent ribbon showing date */}
        <div className="absolute left-0 top-4 flex items-center bg-brand-500 px-3 py-1.5 text-white shadow-md">
          <Calendar className="mr-2 h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          <time
            dateTime={date}
            className="text-xs font-bold uppercase tracking-wider"
          >
            {formatDate(date, currentLang)}
          </time>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink-500">
          <MapPin className="h-3.5 w-3.5 text-brand-500" strokeWidth={2.5} aria-hidden="true" />
          {location}
        </div>

        <h3 className="text-lg md:text-xl font-bold leading-snug text-ink-900">
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              handleNavigate()
            }}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <span className="absolute inset-0" aria-hidden="true" />
            {title}
          </a>
        </h3>

        <div className="mt-auto pt-4 border-t border-ink-200/70 inline-flex items-center gap-2 text-sm font-semibold text-brand-600 transition-colors group-hover:text-brand-700">
          {t('buttons.viewDetails')}
          <ArrowUpRight
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={2.5}
          />
        </div>
      </div>
    </article>
  )
}
